'use client';

import type { QueryFunctionContext} from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import produce from 'immer';
import { useAuth } from 'react-oidc-context';

import { fetchFeedbacks } from '../../../apis/feedbacks/feedbacks';
import { findAndReshape } from '../../../helpers/feedbacks/reshapeFeedback';
import type { FeedbackPageType, FeedbackTopicType } from '../../../types/feedbacks';
import { useFeedbacks } from '../shared/useFeedbacks';

export const useFeedbacksInfiniteQuery = (
  topicId: string,
  topicType: FeedbackTopicType,
  forceDisabled = false,
) => {
  const { isLoading, user: sessionData } = useAuth();
  const { sort, filter } = useFeedbacks();

  return useInfiniteQuery(
    ['feedbacks', topicId, filter, sort],
    async (data: QueryFunctionContext) => {
      const { pageParam = 1 } = data;

      const result = await fetchFeedbacks({
        offset: pageParam,
        topicType,
        topicId,
        sort,
        filter,
        token: sessionData?.access_token,
      });
      const reshaped = produce(result, (draft) => {
        draft.data.allFeedback.forEach((feedback) => {
          if ((feedback.type === 'Comment' || feedback.type === 'Review') && 'commentChildren' in feedback) {
            feedback.commentChildren = findAndReshape(feedback.commentChildren);
          }
        });
      });

      return reshaped.data;
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage: FeedbackPageType, allPages: FeedbackPageType[]) => {
        if (lastPage?.allFeedback?.length < 10 || !lastPage?.hasNext) {
          return;
        }
        return allPages?.length + 1;
      },
      enabled: !forceDisabled && !isLoading,
    },
  );
};
